import { useCallback, useEffect, useMemo, useState } from 'react';
import { pb } from '../lib/pocketbase.js';
import UpdateFormModal from '../components/UpdateFormModal.jsx';
import { formatDatum, userAnzeige } from '../lib/aktivitaet.js';
import {
  fetchAlleUpdates,
  fetchBestaetigungenFuerUpdate,
} from '../lib/updates.js';

export default function AdminUpdatesPage() {
  const [updates, setUpdates] = useState([]);
  const [users, setUsers] = useState([]);
  const [bestaetigungen, setBestaetigungen] = useState({}); // { updateId: bestaetigung[] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, usersList] = await Promise.all([
        fetchAlleUpdates(),
        pb.collection('users').getFullList({ sort: 'nachname,vorname,email' }),
      ]);
      setUpdates(list);
      setUsers(usersList);
    } catch (err) {
      setError(err?.message || 'Updates konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Bestätigungen werden lazy nachgeladen, wenn der Admin eine Zeile aufklappt.
   * So bleibt die Hauptliste schnell, auch wenn es viele Updates gibt.
   */
  const toggleExpand = useCallback(
    async (updateId) => {
      if (expanded === updateId) {
        setExpanded(null);
        return;
      }
      setExpanded(updateId);
      if (!bestaetigungen[updateId]) {
        try {
          const list = await fetchBestaetigungenFuerUpdate(updateId);
          setBestaetigungen((prev) => ({ ...prev, [updateId]: list }));
        } catch (err) {
          console.error('Bestätigungen konnten nicht geladen werden', err);
          setBestaetigungen((prev) => ({ ...prev, [updateId]: [] }));
        }
      }
    },
    [expanded, bestaetigungen]
  );

  function openCreate() {
    setEditTarget(null);
    setModalOpen(true);
  }

  function openEdit(update) {
    setEditTarget(update);
    setModalOpen(true);
  }

  async function handleDelete(update) {
    if (
      !window.confirm(
        `Update "${update.titel}" wirklich löschen? Alle Bestätigungen dazu werden ebenfalls entfernt.`
      )
    ) {
      return;
    }
    try {
      await pb.collection('updates').delete(update.id);
      setUpdates((prev) => prev.filter((u) => u.id !== update.id));
      setBestaetigungen((prev) => {
        const next = { ...prev };
        delete next[update.id];
        return next;
      });
    } catch (err) {
      alert(err?.message || 'Löschen fehlgeschlagen.');
    }
  }

  // Vorberechnete User-Counts: total und je Update wer hat / wer nicht.
  const userCount = users.length;
  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Updates verwalten</h1>
          <p className="mt-1 text-sm text-slate-600">
            Lege neue Changelog-Einträge an und sieh, wer sie schon bestätigt hat.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          + Neues Update
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      )}

      <div className="card space-y-3">
        {loading && <p className="text-sm text-slate-500">Lade …</p>}
        {!loading && updates.length === 0 && (
          <p className="text-sm text-slate-500">Noch keine Updates angelegt.</p>
        )}

        {updates.map((update) => {
          const istOffen = expanded === update.id;
          const liste = bestaetigungen[update.id] ?? null;
          const bestaetigtSet = new Set((liste ?? []).map((b) => b.user));
          const bestaetigtCount = liste ? liste.length : null;
          return (
            <div
              key={update.id}
              className="rounded-lg border border-slate-200 bg-white"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 p-4">
                <button
                  type="button"
                  onClick={() => toggleExpand(update.id)}
                  className="min-w-0 flex-1 text-left"
                  aria-expanded={istOffen}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {!update.aktiv && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
                        inaktiv
                      </span>
                    )}
                    {update.version && (
                      <span className="text-xs font-medium text-slate-500">
                        Version {update.version}
                      </span>
                    )}
                    {update.veroeffentlicht_am && (
                      <span className="text-xs text-slate-400">
                        · {formatDatum(update.veroeffentlicht_am)}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-base font-semibold text-slate-900">
                    {update.titel}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {bestaetigtCount === null ? (
                      <>Bestätigungen anzeigen …</>
                    ) : (
                      <>
                        {bestaetigtCount} von {userCount} User
                        {userCount === 1 ? '' : 'n'} bestätigt
                      </>
                    )}
                  </div>
                </button>
                <div className="flex flex-shrink-0 gap-3">
                  <button
                    type="button"
                    className="text-sm font-medium text-primary-600 hover:text-primary-800"
                    onClick={() => openEdit(update)}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="text-sm font-medium text-red-600 hover:text-red-800"
                    onClick={() => handleDelete(update)}
                  >
                    Löschen
                  </button>
                </div>
              </div>

              {istOffen && (
                <div className="space-y-4 border-t border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-emerald-800">
                      ✓ Bestätigt ({liste?.length ?? 0})
                    </h3>
                    {liste === null ? (
                      <p className="text-xs text-slate-500">Lade …</p>
                    ) : liste.length === 0 ? (
                      <p className="text-xs text-slate-500">Noch niemand.</p>
                    ) : (
                      <ul className="divide-y divide-slate-200 rounded-md bg-white ring-1 ring-slate-200">
                        {liste.map((b) => {
                          const u = b.expand?.user ?? usersById.get(b.user);
                          return (
                            <li
                              key={b.id}
                              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                            >
                              <span className="font-medium text-slate-800">
                                {userAnzeige(u)}
                              </span>
                              <span className="text-xs text-slate-500">
                                {formatDatum(b.bestaetigt_am)}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-amber-800">
                      ✗ Noch offen (
                      {liste === null ? '…' : Math.max(0, userCount - liste.length)})
                    </h3>
                    {liste === null ? (
                      <p className="text-xs text-slate-500">Lade …</p>
                    ) : (
                      (() => {
                        const offen = users.filter((u) => !bestaetigtSet.has(u.id));
                        if (offen.length === 0) {
                          return (
                            <p className="text-xs text-emerald-700">
                              Alle User haben bestätigt.
                            </p>
                          );
                        }
                        return (
                          <ul className="divide-y divide-slate-200 rounded-md bg-white ring-1 ring-slate-200">
                            {offen.map((u) => (
                              <li
                                key={u.id}
                                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                              >
                                <span className="font-medium text-slate-800">
                                  {userAnzeige(u)}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {u.email}
                                </span>
                              </li>
                            ))}
                          </ul>
                        );
                      })()
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <UpdateFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        update={editTarget}
        onSaved={() => load()}
      />
    </div>
  );
}
